import { Injectable }       from '@angular/core';
import { NgbModal }         from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';

import { ApiService }                    from '../api/api.service';
import { DomainParserService }           from '../core/services/domain.parser.service';
import { JsonService }                   from '../core/services/json.service';
import { QuickbooksAccountsModel }       from './typings/quickbooks.accounts';
import { QuickbooksDisclaimerComponent } from './quickbooks.disclaimer.component';
import { QuickbooksSSOSessionModel }     from './typings/quickbooks.sso-session';
import { QuickbooksSSOSettingsModel }    from './typings/quickbooks.sso-settings';
import { QuickbooksTaxRateModel }        from './typings/quickbooks.tax-rate';
import { SessionService }                from '../sessions/session.service';
import { SyncQuickbooksComponent }       from './quickbooks.sync.component';
import { UserModel }                     from '../users/models/user';

@Injectable({
  providedIn: 'root'
})
export class QuickbooksService {
  constructor (
    private apiService         : ApiService,
    private domainParserService: DomainParserService,
    private jsonService        : JsonService,
    private modalService       : NgbModal,
    private sessionService     : SessionService,
    private translateService   : TranslateService
  ) {}

  authenticate ( settings : QuickbooksSSOSettingsModel ) : Promise<QuickbooksSSOSessionModel> {
    return this.apiService.post('/quickbooks_online/create_from_signup', settings);
  }

  authorize () : Promise<any> {
    if (this.sessionService.company().has_xero) {
      return Promise.reject(this.translateService.instant('JS_SPACE.INTEGRATIONS.ACCOUNTING_UNAUTH_FIRST', {
        name1: this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.QUICKBOOKS.QUICKBOOKS_ONLINE'),
        name2: 'Xero'
      }));
    }
    else if (this.sessionService.company().has_myob) {
      return Promise.reject(this.translateService.instant('JS_SPACE.INTEGRATIONS.ACCOUNTING_UNAUTH_FIRST', {
        name1: this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.QUICKBOOKS.QUICKBOOKS_ONLINE'),
        name2: 'MYOB'
      }));
    } else {
      return this.apiService.get('/quickbooks_online/is_unlinked')
      .then(response => response ? this.launchDisclaimer() : Promise.resolve());
    }
  }

  checkExistingIntegrations () : Promise<any> {
    return this.apiService.get('/company/accounting_ids_exist/qbo');
  }

  clearID () : Promise<any> {
    return this.apiService.post('/company/clear_accounting_contact_ids', {
      accounting_integration: 'qbo',
      confirmation          : 'CLEAR IDS'
    });
  }

  getAccounts () : Promise<QuickbooksAccountsModel> {
    return this.apiService.get('/quickbooks_online/accounts');
  }

  getAuthenticationUrl () : Promise<string> {
    return this.apiService.get('/quickbooks_online/authenticate_from_qbo')
    .then(( response : any ) => response.url);
  }

  getSignupUrlFromCountry ( country : string, queryParams : any = this.jsonService.getQueryParameters() ) : Promise<string> {
    const tag = `/login.html#/quickbooks-signup/${ country }`;

    const queryString = Object.keys(queryParams)
    .map(( key : string ) => `${ key }=${ encodeURIComponent(queryParams[ key ]) }`)
    .join('&');

    return Promise.resolve(`${ this.domainParserService.getDomain(country) }${ tag }?${ queryString }`);
  }

  getSSOSettings ( queryParams : any = this.jsonService.getQueryParameters() ) : Promise<QuickbooksSSOSettingsModel> {
    let settings : QuickbooksSSOSettingsModel = queryParams;

    return this.apiService.post('/quickbooks_online/exchange_code_for_tokens', {
      code: settings.code
    })
    .then(( tokens : QuickbooksSSOSettingsModel ) => {
      settings = angular.extend(settings, tokens);

      return settings;
    });
  }

  getTaxRates () : Promise<Array<QuickbooksTaxRateModel>> {
    return this.apiService.get('/quickbooks_online/tax_rates')
    .then(( response : any ) => response.all_tax_rates);
  }

  getTitle () : string {
    if (this.isDisconnecting()) {
      return 'GENERAL_SPACE.MESSAGE.NOW_DISCONNECTING_YOU';
    }

    if (this.isLoginReady()) {
      return 'GENERAL_SPACE.MESSAGE.LOGGING_YOU_IN';
    }

    return 'GENERAL_SPACE.MESSAGE.NOW_CONNECTING_YOU';
  }

  isDisconnecting ( queryParams : any = this.jsonService.getQueryParameters() ) : boolean {
    return queryParams.disconnect === 'true';
  }

  isLoginReady ( queryParams : any = this.jsonService.getQueryParameters() ) : boolean {
    return queryParams.qbo_login === 'true' &&
      queryParams.qbo_sso_token;
  }

  launchDisclaimer () : Promise<any> {
    return this.modalService.open(QuickbooksDisclaimerComponent).result;
  }

  launchSSO () : Promise<string> {
    return this.apiService.get('/quickbooks_online/authenticate', null, null, {
      params: {
        disconnect: this.isDisconnecting()
      }
    })
    .then(response => response.url);
  }

  login ( settings : QuickbooksSSOSessionModel = this.jsonService.getQueryParameters() ) : Promise<string> {
    let session;

    return Promise.resolve()
    .then(() => this.apiService.post('/sessions/qbo', settings))
    .then(( response : any ) => {
      session = response;

      return this.sessionService.setSessionAndValidate(session);
    })
    .then(( rights : any ) => this.isDisconnecting()
      ? this.apiService.get('/company/remove_qbo/')
      : Promise.resolve(true))
    .then(() => this.isDisconnecting()
      ? Promise.resolve('/workshop.html#/quickbooks_settings')
      : session.super_user
        ? Promise.resolve('/workshop.html#/admin_dashboard')
        : Promise.resolve('/workshop.html#/dashboard'));
  }

  needsAuthentication ( queryParams : any = this.jsonService.getQueryParameters() ) : boolean {
    return !this.isDisconnecting(queryParams)
      && !queryParams.qbo_sso_token
      && !queryParams.realmId
      && !queryParams.code
      && !queryParams.state;
  }

  shareAccount ( shareAccountFlag : boolean ) : Promise<any> {
    return this.apiService.get('/company/share_accounting', [ shareAccountFlag ? 't' : 'f' ]);
  }

  signup ( user : UserModel = {}, settings : QuickbooksSSOSettingsModel ) : Promise<boolean> {
    const params : any = angular.extend(user, settings);

    return this.apiService.post('/subscriptions/quickbooks_setup', params);
  }

  sync () : Promise<any> {
    return this.modalService.open(SyncQuickbooksComponent).result;
  }
}